import dayjs from 'dayjs';
import { Document, DOCUMENT_TYPES } from 'commons/types/documentsTypes';
import {
  Meeting,
  SOON_DIFF,
  MEETING_TYPES,
  MEETING_STATUSES,
  MEETING_CARD_STATUSES,
  MeetingType,
  MeetingCardStatus,
} from 'commons/types/meetingsTypes';

import { PATIENT_STATUSES } from 'commons/types/patientsTypes';
import { Service } from 'commons/types/servicesTypes';

export function isMeetingSoon(meeting: Meeting | undefined): boolean {
  return meeting
    ? dayjs(meeting.start_date).subtract(SOON_DIFF, 'minutes').isBefore(dayjs())
    : false;
}

export function isMeetingPast(meeting: Meeting | undefined) {
  if (meeting?.type === MEETING_TYPES.RDV_3) {
    return (
      (!!meeting && dayjs(meeting.end_date).isBefore(dayjs())) ||
      meeting?.status === MEETING_STATUSES.REFUNDED
    );
  }
  return !!meeting && dayjs(meeting.start_date).isBefore(dayjs());
}

export function isNextMeetingIsScheduled(
  meeting: Meeting | undefined,
): boolean {
  return (
    !!meeting &&
    [
      MEETING_TYPES.RDV_1,
      MEETING_TYPES.RDV_2,
      MEETING_TYPES.RDV_3,
      MEETING_TYPES.RDV_PPC,
      MEETING_TYPES.RDV_SWITCH,
    ].some((type) => type === meeting.type) &&
    !isMeetingPast(meeting)
  );
}

export function getNextMeetingType(
  meeting?: Meeting,
  status?: string,
  skipToRDV2: boolean = false,
  isPSAD: boolean = false,
) {
  if (isPSAD) {
    return !meeting ? MEETING_TYPES.RDV_SWITCH : MEETING_TYPES.None;
  }
  if (!meeting) {
    return skipToRDV2 ? MEETING_TYPES.RDV_2 : MEETING_TYPES.RDV_1;
  }
  if (meeting.status === MEETING_STATUSES.CANCELED) {
    return meeting.type;
  }
  if (isNextMeetingIsScheduled(meeting)) {
    return MEETING_TYPES.None;
  }
  if (meeting.type === MEETING_TYPES.RDV_1) {
    return meeting.locks.rdv_1 ? MEETING_TYPES.RDV_1 : MEETING_TYPES.RDV_2;
  }
  if (meeting.type === MEETING_TYPES.RDV_2) {
    if (meeting.locks.rdv_2 || meeting.locks.reschedule_rdv_2) {
      return MEETING_TYPES.RDV_2;
    }
    return MEETING_TYPES.RDV_3;
  }
  if (meeting.type === MEETING_TYPES.RDV_3) {
    if (status === PATIENT_STATUSES.PPC) {
      return MEETING_TYPES.RDV_PPC;
    }
    if (meeting.locks.rdv_3) {
      return MEETING_TYPES.RDV_3;
    }
    return MEETING_TYPES.None;
  }
  if (meeting.type === MEETING_TYPES.RDV_PPC) {
    return meeting.locks.rdv_4 ? MEETING_TYPES.RDV_PPC : MEETING_TYPES.None;
  }
  return MEETING_TYPES.None;
}

export function meetingStatus(meeting: Meeting | undefined) {
  if (!meeting) {
    return '';
  }

  const lock = Object.entries(meeting.locks).find((entry) => entry[1]);
  if (lock) {
    switch (lock[0]) {
      case 'rdv_1':
      case 'rdv_2':
      case 'rdv_3':
      case 'rdv_4':
      case 'rdv_switch':
        return 'Non venu';
      case 'reschedule_rdv_2':
        return 'Examen non exploitable';
      default:
        break;
    }
  }
  if (meeting.status === MEETING_STATUSES.CANCELED) {
    return 'Annulé';
  }
  if (meeting.status === MEETING_STATUSES.REFUNDED) {
    return 'Remboursé';
  }
  if (meeting.type === MEETING_TYPES.RDV_1) {
    return 'Rendez-vous 1';
  }
  if (meeting.type === MEETING_TYPES.RDV_2) {
    return 'Rendez-vous 2';
  }
  if (meeting.type === MEETING_TYPES.RDV_3) {
    return 'Rendez-vous 3';
  }
  if (meeting.type === MEETING_TYPES.RDV_PPC) {
    return 'Rendez-vous de suivi';
  }
  if (meeting.type === MEETING_TYPES.RDV_SWITCH) {
    return 'Rendez-vous SWITCH';
  }
  return 'Etat inconnu';
}
export function getMeetingCardState(
  meeting?: Meeting,
  documents?: Document[],
  status?: string,
  skipToRDV2?: boolean,
  isPSAD?: boolean,
) {
  const nextSchedule = getNextMeetingType(meeting, status, skipToRDV2, isPSAD);
  const hasPrescription = documents?.some(
    (document) => document.type === DOCUMENT_TYPES.ORDONNANCE,
  );
  const hasAttestation = documents?.some(
    (document) => document.type === DOCUMENT_TYPES.JUSTIFICATIF_CMU,
  );
  const isPast = isMeetingPast(meeting);
  if (nextSchedule === MEETING_TYPES.None) {
    if (meeting?.type === MEETING_TYPES.RDV_3) {
      if (!hasPrescription && !meeting?.metadata?.isMigrate) {
        return MEETING_CARD_STATUSES.TO_JUSTIFY_PRESCRIPTION;
      }
      if (meeting?.metadata.hasCMU && !hasAttestation) {
        return MEETING_CARD_STATUSES.TO_JUSTIFY_CMU;
      }

      if (isPast) {
        if (meeting?.locks.rdv_3) {
          return MEETING_CARD_STATUSES.CANCELED_TO_SCHEDULE_3;
        }
      }
    }
    if (meeting?.patient?.metadata?.status === PATIENT_STATUSES.PSG) {
      return MEETING_CARD_STATUSES.TO_SCHEDULE_PSG;
    }
    return isPast ? MEETING_CARD_STATUSES.FINISH : MEETING_CARD_STATUSES.OK;
  }

  if (nextSchedule === MEETING_TYPES.RDV_1) {
    if (meeting?.locks.rdv_1) {
      return MEETING_CARD_STATUSES.CANCELED_TO_SCHEDULE_1;
    }
    return MEETING_CARD_STATUSES.TO_SCHEDULE_1;
  }
  if (nextSchedule === MEETING_TYPES.RDV_2) {
    if (meeting?.locks.rdv_2) {
      return MEETING_CARD_STATUSES.CANCELED_TO_SCHEDULE_2;
    }
    if (meeting?.locks.reschedule_rdv_2) {
      return MEETING_CARD_STATUSES.TO_RESCHEDULE_2;
    }
    return MEETING_CARD_STATUSES.TO_SCHEDULE_2;
  }
  if (nextSchedule === MEETING_TYPES.RDV_3) {
    if (meeting?.locks.rdv_3) {
      return MEETING_CARD_STATUSES.CANCELED_TO_SCHEDULE_3;
    }
    if (meeting?.locks.rdv_3_ok_lancement) {
      return MEETING_CARD_STATUSES.TO_SCHEDULE_3;
    }
    return MEETING_CARD_STATUSES.WAITING_FOR_VALIDATION_RDV_3;
  }
  if (nextSchedule === MEETING_TYPES.RDV_PPC) {
    if (meeting?.locks.rdv_4) {
      return MEETING_CARD_STATUSES.CANCELED_TO_SCHEDULE_PPC;
    }
    if (meeting?.locks.transmission_psad_ok) {
      return MEETING_CARD_STATUSES.TO_SCHEDULE_PPC;
    }
    return MEETING_CARD_STATUSES.TO_SEND_PSAD;
  }
  if (nextSchedule === MEETING_TYPES.RDV_SWITCH) {
    if (meeting?.locks.rdv_switch) {
      return MEETING_CARD_STATUSES.CANCELED_TO_SCHEDULE_SWITCH;
    }
    return MEETING_CARD_STATUSES.TO_SCHEDULE_SWITCH;
  }
  return MEETING_CARD_STATUSES.FINISH;
}

export function statusToHubspot(
  status: MeetingCardStatus,
  type: MeetingType | undefined,
) {
  switch (status) {
    case MEETING_CARD_STATUSES.OK:
      switch (type) {
        case MEETING_TYPES.RDV_1:
          return '238638531'; // RDV 1 - Planifié`;
        case MEETING_TYPES.RDV_2:
          return '238633970'; // RDV 2 - Planifié`;
        case MEETING_TYPES.RDV_3:
          return '238638537'; // RDV 3 - Planifié`;
        case MEETING_TYPES.RDV_PPC:
          return '238638542'; // RDV PPC - Planifié`;
        case MEETING_TYPES.RDV_SWITCH:
          return '422019296'; // RDV SWITCH - Planifié`;
        default:
          return '';
      }
    case MEETING_CARD_STATUSES.TO_JUSTIFY_PRESCRIPTION:
      return '238633973'; // `${type} - Planifié - en attente d'ordonnance`;
    case MEETING_CARD_STATUSES.TO_SCHEDULE_1:
      return ''; // `RDV 1 - A Planifié`;
    case MEETING_CARD_STATUSES.TO_SCHEDULE_2:
      return ''; // `RDV 2 - A Planifié`;
    case MEETING_CARD_STATUSES.TO_SCHEDULE_3:
      return '238633971'; // `RDV 2 - PG OK Machine OK`;
    case MEETING_CARD_STATUSES.TO_SCHEDULE_SWITCH:
      return '423125733'; // `RDV SWITCH - A Planifié`;
    case MEETING_CARD_STATUSES.TO_RESCHEDULE_2:
      return '238633972'; // `RDV 2 - A refaire`;
    case MEETING_CARD_STATUSES.TO_JUSTIFY_CMU:
      return '238638538'; // `RDV 3 - Planifié - en attente justificatifs`;
    case MEETING_CARD_STATUSES.TO_SCHEDULE_PPC:
      return '238638541'; // `RDV 3 - PPC PSAD OK`;
    case MEETING_CARD_STATUSES.WAITING_FOR_VALIDATION_RDV_3:
      return '258551271'; // `RDV 3 - Blocage Hapni`;
    case MEETING_CARD_STATUSES.TO_SEND_PSAD:
      return '258539000'; // `RDV 3 - PPC`;
    case MEETING_CARD_STATUSES.TO_SCHEDULE_PSG:
      return '238638540'; // `RDV 3 - PSG`;
    case MEETING_CARD_STATUSES.CANCELED_TO_SCHEDULE_1:
      return '238638532'; // `RDV 1 - Non-venu`;
    case MEETING_CARD_STATUSES.CANCELED_TO_SCHEDULE_2:
      return '238638534'; // `RDV 2 - Non-venu`;
    case MEETING_CARD_STATUSES.CANCELED_TO_SCHEDULE_3:
      return '238638539'; // `RDV 3 - Non-venu`;
    case MEETING_CARD_STATUSES.CANCELED_TO_SCHEDULE_PPC:
      return '238633976'; // `RDV PPC - Non-venu`;
    case MEETING_CARD_STATUSES.CANCELED_TO_SCHEDULE_SWITCH:
      return '422008055'; // `RDV SWITCH - Non-venu`;
    case MEETING_CARD_STATUSES.FINISH:
      switch (type) {
        case MEETING_TYPES.RDV_3:
          return '238633975'; // RDV 3 - Autre`;`;
        case MEETING_TYPES.RDV_PPC:
          return '238633977'; // RDV suivi  - Suivi PPC realisé`;`;
        case MEETING_TYPES.RDV_SWITCH:
          return '422008054'; // RDV SWITCH - Realisé;`;
        default:
          return '';
      }
    default:
      return '238633968'; // `Lead sans RDV`;
  }
}

export function serviceFromZip(services: Service[] | undefined, zip: string) {
  const slicedZip = zip.slice(0, 2);
  const service = services?.find((s) => s.zips.includes(slicedZip)) || null;
  if (service) {
    return service.amelia_id;
  }
  return '';
}
